import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { track } from "@/lib/analytics";

type Props = {
  children: ReactNode;
  title?: string;
  description?: string;
  /** Logical name of the boundary — helps identify which component tree failed */
  scope?: string;
  fallback?: (err: Error, reset: () => void) => ReactNode;
};

type State = { error: Error | null; info: ErrorInfo | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const scope = this.props.scope ?? "unknown";
    // Extract the top-most React component name from the stack for pinpointing
    const stack = info?.componentStack ?? "";
    const firstComponent = stack
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .find((l) => l.startsWith("at "));

    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${scope}]`, error, stack);

    try {
      track("client_render_error", {
        scope,
        message: error.message,
        name: error.name,
        stack: (error.stack ?? "").slice(0, 4000),
        component_stack: stack.slice(0, 4000),
        top_component: firstComponent ?? null,
        url: typeof window !== "undefined" ? window.location.href : null,
        ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch { /* swallow telemetry errors */ }

    this.setState({ info });
  }

  private reset = () => this.setState({ error: null, info: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    const title = this.props.title ?? "Something went wrong";
    const description = this.props.description ?? "We hit a snag loading this section. You can retry, or reload the page.";

    return (
      <div className="grid place-items-center py-16">
        <Card className="max-w-lg border-destructive/20 bg-card p-6 text-center shadow-card">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-display text-xl">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          <details className="mt-3 rounded-md bg-muted/50 p-2 text-left text-[11px] text-muted-foreground">
            <summary className="cursor-pointer font-medium">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words">{error.message}</pre>
          </details>
          <div className="mt-4 flex justify-center gap-2">
            <Button size="sm" onClick={this.reset}><RefreshCw className="mr-1 h-3 w-3" />Try again</Button>
            <Button size="sm" variant="outline" onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </Card>
      </div>
    );
  }
}
